import { PropsWithChildren } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import styled, { css } from "styled-components";
import { translate } from "../../intl";
import { RadioButton } from "../../ui/RadioButton";
import { BaseField, BaseFieldProps } from "./BaseField";
import { HiddenField } from "./HiddenField";

export type OptionsProps = {
  label: string;
  value?: boolean | string | number;
  color?: string;
};

const Container = styled.div<{ isHorizontal: boolean }>`
  min-height: 36px;
  display: flex;

  ${({ isHorizontal }) =>
    isHorizontal
      ? css`
          align-items: center;

          .radio-button {
            margin-right: 16px;
          }
        `
      : css`
          flex-direction: column;

          .radio-button {
            margin-bottom: 8px;
          }
        `}
`;

export type RadioLayout = "horizontal" | "vertical";
export interface RadioGroupFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends BaseFieldProps<TFieldValues, TName> {
  options: OptionsProps[];
  layout?: RadioLayout;
  disabled?: boolean;
}

export function RadioGroupField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  label,
  options,
  defaultValue,
  rules,
  form,
  layout,
  readOnly,
  disabled,
}: PropsWithChildren<RadioGroupFieldProps<TFieldValues, TName>>) {
  const returnValue = (value: string | boolean): string => {
    return (
      options.find((option) => {
        const optionValue = option.value ?? option.label;
        return optionValue === value;
      })?.label ?? "-"
    );
  };
  return (
    <BaseField
      form={form}
      name={name}
      label={label}
      defaultValue={defaultValue}
      rules={rules}
      fieldClassName="radio-group-field"
      render={({ field }) =>
        readOnly ? (
          <>
            <HiddenField valueState={field.value} name={name} form={form} />
            <div>{returnValue(field.value)}</div>
          </>
        ) : (
          <Container isHorizontal={layout === "horizontal"}>
            {options.map((option) => {
              const optionValue = option.value ?? option.label;
              return (
                <RadioButton
                  key={`${name}-${optionValue}`}
                  name={name}
                  label={translate(`${option.label}`)}
                  value={optionValue}
                  onChange={field.onChange}
                  checked={optionValue === field.value}
                  color={option.color}
                  disabled={disabled}
                />
              );
            })}
          </Container>
        )
      }
    />
  );
}

RadioGroupField.defaultProps = {
  defaultValue: "",
  layout: "vertical",
};
